import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TixxSelectService} from '~core/services/tixx-select/tixx-select.service';
import {TixxSelectTranslationsService} from '~shared/translations/alerts/tixx-select-overview-translations.service';
import {NotificationType} from '~core/enum/notification.enum';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {noWhitespaceCharacters} from '~shared/directives/form-validators';
import {Lottery} from '~core/models';
import {DefaultNotificationService} from '~core/services';
import {EventAdministrationService} from "~features/tixx-select/event-administration.service";

/**
 * Workspace sidebar for editing customer data
 */
@UntilDestroy()
@Component({
  selector: 'app-lottery-details',
  templateUrl: './lottery-details.component.html',
})
export class LotteryDetailsComponent implements OnInit, OnDestroy {
  @Input() public lottery: Lottery;

  public updateLotteryForm: UntypedFormGroup;
  public spinnerName: string = 'lotteryDetailsSubmit';
  public yesOrNoDropdown: Array<any> = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private notificationService: DefaultNotificationService,
    private tixxSelectService: TixxSelectService,
    public tixxSelectOverviewService: EventAdministrationService,
    public tixxSelectTranslationService: TixxSelectTranslationsService,
    public flTranslations: FormLabelTranslationService,
    public buttonsTranslations: ButtonsTranslations,
    public spinnerService: SpinnerService,
  ) {
  }

  public ngOnInit(): void {
    this.yesOrNoDropdown.push(
      {value: true, status: this.flTranslations.i18nYes},
      {value: false, status: this.flTranslations.i18nNo},
    );

    this.fillLotteryForm();
  }

  public ngOnDestroy(): void {
    this.tixxSelectOverviewService.toggleEditLotteryDetailsModal(false);
  }

  /**
   * Create the lottery form.
   */
  public fillLotteryForm(): void {
    const event = this.lottery.event;
    this.updateLotteryForm = this.formBuilder.group({
      tixxId: [event.tixxId],
      name: [event.name, [
        Validators.required,
        noWhitespaceCharacters
      ]],
      competition: [event.competition, [
        Validators.required,
        noWhitespaceCharacters
      ]],
      flexibleDate: [event.flexibleDate],
      date: [event.date.dateIso],
      homeTeam: [event.homeTeam],
      guestTeam: [event.guestTeam],
      heroImage: [event.heroImage],
      guidelineHeadline: [event.guidelineHeadline],
      guidelineText: [event.guidelineText],
      venue: [event.venue, [
        Validators.required,
        noWhitespaceCharacters
      ]],
      specialRequirementText: [event.specialRequirementText],
      specialRequirementDescription: [event.specialRequirementDescription],
      type: [{value: event.type.name, disabled: true}],
    });
  }

  /**
   * If lottery form is valid update the lottery, otherwise display the errors.
   */
  public updateFormSubmit(form: UntypedFormGroup): void {
    if (form.valid) {
      form.value.date = new Date(form.value.date);
      const completedForm = {
        event: form.value,
      };
      delete completedForm.event.type;
      this.spinnerService.show(this.spinnerName);
      this.tixxSelectService.updateLottery(this.lottery.id, completedForm).pipe(untilDestroyed(this))
        .subscribe({
          next: (data: any) => {
            this.tixxSelectService.setSelectedLottery(data);
            this.spinnerService.hide(this.spinnerName);
            this.tixxSelectOverviewService.toggleEditLotteryDetailsModal(false);
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.tixxSelectTranslationService.i18nSuccessUpdateLottery
            });
            this.fillLotteryForm();
          },
          error: (error: Error) => {
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.tixxSelectTranslationService.i18nErrorUpdateLottery
            });
            this.spinnerService.hide(this.spinnerName);
          }
        });
    }
  }

}
